<template>
  <div>
    <CreateNavBar/>
    <div class="container">
      <CreateTitle :title="page_title"/>

      <div class="create">
        <loader :isLoading="isRequestInProgress"/>
        <div class="row">
          <div class="col-sm">
            <div v-if="isTakeSelfie" class="selfie-cam">
              <video ref="video" id="video" width="350px" height="350px" autoplay playsinline></video>
              <canvas ref="canvas" id="canvas" width="350px" height="350px" hidden></canvas>
            </div>
            <div v-else>
              <img v-if="imageData === assetImg" :src="imageData"
                alt="Create Account for YeyPass image">
              <img v-else :src="imageData" alt="Create Account for YeyPass image">
            </div>
          </div>

          <div class="col-sm form-container">
            <div id="selfie-container">
              <p class="selfie-info">
                Please take a photo of yourself facing front at the camera.
                Center yourself in the middle of the screen & smile! :)
                This photo will also be used for verifying your profile.
              </p>

              <div class="text-center">
                <i class="fas fa-camera"></i>
                <button class="btn btn-selfie" v-if="!isTakeSelfie" @click="takeSelfie">
                  Take a Selfie
                </button>
                <button class="btn btn-selfie" v-else v-on:click="capture()">
                  Snap Photo
                </button>
              </div>

              <!-- Submit Button -->
              <div class="text-center">
                <small class="form-text text-muted" data-type="message" v-if="error_message">
                  {{ error_message }}
                </small>

                <button @click="finish()" type="submit" class="btn btn-primary">
                  Finish
                </button>
                <br>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
import CreateNavBar from '@/components/common/CreateNavBar'
import CreateTitle from '@/components/common/CreateTitle'
import Loader from '@/components/common/Loader'

import { SELFIE } from '@/api/constants/ImageAssets'
import { APIService } from '@/api/APIService'
import { AuthService } from '@/api/AuthService'

const api = new APIService()
const auth = new AuthService()

const assetImage = SELFIE

// Reference: https://jsfiddle.net/fzrLvbwf/11/
// https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/drawImage
export default {
  components: {
    CreateNavBar,
    CreateTitle,
    Loader
  },
  data() {
    return {
      page_title: 'Take a Selfie',
      assetImg: assetImage,
      imageData: assetImage,
      form: {
        gender: 'none',
        mobile_number: 'none',
        birthdate: 'none',
        occupation: 'none',
        country: 'none',
        province: 'none',
        city: 'none',
        present_address: 'none',
        permanent_address: 'none',
        profile_picture: 'none'
      },
      user_id: auth.getUserId(),
      error: '',
      error_message: '',
      pageOrigin: '',
      route: '/dashboard',
      isRequestInProgress: false,
      // for camera
      video: {},
      canvas: {},
      isTakeSelfie: false,
      isStreaming: false,
      stream: {}
    }
  },
  mounted() {
    this.pageOrigin = this.$route.query.page_origin
    if (this.pageOrigin) {
      this.route = this.pageOrigin.replace(window.location.origin, '')
    }
  },
  destroyed() {
    this.stopStreaming()
  },
  methods: {
    stopStreaming() {
      if (this.isStreaming) {
        this.stream.getTracks().forEach(track => { track.stop() })
        this.video.srcObject = null
      }
      this.isStreaming = false
    },
    takeSelfie() {
      this.isTakeSelfie = true
      this.isStreaming = true
      let constraints = { video: true }
      navigator.mediaDevices
        .getUserMedia(constraints)
        .then(this.handleSuccess)
        .catch(this.handleError)
    },
    handleSuccess(stream) {
      this.video = this.$refs.video
      this.stream = stream
      window.stream = stream
      this.video.srcObject = stream
    },
    handleError(error) {
      this.error_message = error
    },
    hasGetUserMedia() {
      return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
    },
    capture() {
      this.canvas = this.$refs.canvas
      const canvasCtx = this.canvas.getContext('2d')

      var xpos
      var ypos
      var clipHeight
      var clipWidth

      var canvasWidth = this.canvas.width
      var canvasHeight = this.canvas.height

      // (source, start source(clip) xpos, start source(clip) ypos,
      // width of source(clip), height of source(clip), canvas x pos,
      // canvas y pos, canvas width, canvas height)
      // https://www.w3schools.com/tags/canvas_drawimage.asp
      canvasCtx.imageSmoothingEnabled = false

      var captureWidth = this.video.videoWidth
      var captureHeight = this.video.videoHeight

      if (captureWidth > captureHeight) { // it's landscape
        xpos = (captureWidth - captureHeight) / 2
        ypos = 0
        clipWidth = captureHeight
        clipHeight = captureHeight
      } else if (captureWidth < captureHeight) { // it's portrait
        xpos = 0
        ypos = (captureHeight - captureWidth) / 2
        clipWidth = captureWidth
        clipHeight = captureWidth
      } else { // it's a square
        xpos = 0
        ypos = 0
        clipWidth = captureWidth
        clipHeight = captureHeight
      }

      canvasCtx.drawImage(
        this.video, xpos, ypos, clipWidth, clipHeight,
        0, 0, canvasWidth, canvasHeight
      )

      let base64URL = this.canvas.toDataURL('image/png')
      this.imageData = base64URL
      this.isTakeSelfie = false
    },
    finish() {
      var self = this
      if (this.imageData === this.assetImg) {
        this.error_message = 'Please submit a selfie'
        return
      }

      self.isRequestInProgress = true
      var fields = { image_url: this.imageData }
      this.stopStreaming()

      api.uploadImage(fields)
        .then(data => {
          self.form.profile_picture = data.object_name
          self.getProfileDetails()
        })
        .catch(err => {
          self.isRequestInProgress = false
          self.error = err
        })
    },
    getProfileDetails() {
      var self = this
      this.isRequestInProgress = true
      api.getProfileDetails(this.user_id)
        .then(data => {
          self.updateProfileFields(data.profile_details)
        })
        .catch(err => {
          self.isRequestInProgress = false
          self.error_message = err
        })
    },
    updateProfileFields(existingFields) {
      var self = this
      var pic = { profile_picture: self.form.profile_picture }
      var fields = Object.assign(existingFields, pic)
      var keys = [
        'gender',
        'mobile_number',
        'birthdate',
        'occupation',
        'country',
        'province',
        'city',
        'present_address',
        'permanent_address'
      ]

      keys.map(e => { if (fields[e] === undefined) fields[e] = 'none' })

      var postFields = { profile_details: { ...fields } }
      api.updateProfile(this.user_id, postFields)
        .then(data => {
          if (data) {
            if (data.statusCode === 200) {
              self.isRequestInProgress = false
              this.redirectToPage()
            } else {
              self.error_message = 'Something went wrong here'
              self.isRequestInProgress = false
            }
          } else {
            self.error_message = 'Something went wrong here'
            self.isRequestInProgress = false
          }
        })
        .catch(err => {
          self.isRequestInProgress = false
          self.error_message = err
        })
    },
    redirectToPage() {
      this.stopStreaming()
      this.$router.push(this.route)
    }
  }
}
</script>

<style>
@import '../../assets/styles/form-style-1.css';
</style>

<style scoped>
.btn-selfie {
  font-weight: bold;
  color: #007bff;
  padding-left: 0px;
}
svg {
  color: #007bff;
}
</style>



// WEBPACK FOOTER //
// src/containers/individual_profile/CreateProfilePhoto.vue